import { Alert, Button, LinkButton, PageHeader, problemText, Section, TextField } from "@emdur/ui";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useShowOrganization } from "../components/currentOrganization";
import { DeactivateOrganization, RestoreOrganization } from "../components/DeactivateOrganization";
import { call } from "../lib/api";
import { startSignIn } from "../lib/auth";
import { errorText } from "../lib/errors";
import { guard } from "../lib/guard";
import { canDeactivate, canRestore, kindLabel, type Org, stateLabel } from "../lib/orgs";

async function json<T>(r: Response): Promise<T> {
  if (!r.ok) throw new Error(await problemText(r));
  return (await r.json()) as T;
}

export function Organization() {
  const { id = "" } = useParams();
  const base = `/v1/organizations/${id}`;
  const [org, setOrg] = useState<Org | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [email, setEmail] = useState("");
  const [hookUrl, setHookUrl] = useState("https://console.emdur.com/hooks/rehearsal");
  const [hook, setHook] = useState<{ id: string; secret?: string } | null>(null);

  const run = (work: Promise<unknown>) => guard(work, () => startSignIn(`/orgs/${id}`), setError);

  useEffect(() => void run(call("GET", base).then((r) => json<Org>(r)).then(setOrg)), [base]);

  const invite = () => run(call("POST", `${base}/invitations`, { email, roles: ["admin"] }).then(json).then(() => setMessage(`Invitation sent to ${email}.`)));

  const registerHook = () =>
    run(call("POST", `${base}/webhook-endpoints`, { url: hookUrl, event_types: ["io.emdur.developer.client.created"] }).then((r) => json<{ id: string; secret: string }>(r)).then(setHook));

  const sendTest = () => run(call("POST", `${base}/webhook-endpoints/${hook!.id}/test-events`).then(json).then(() => setMessage("Test event queued.")));

  const deactivated = (done: Org) => {
    setOrg(done);
    setMessage(`${done.display_name} is deactivated. Its members and clients can no longer reach it; its records are kept.`);
  };
  const restored = (done: Org) => {
    setOrg(done);
    setMessage(
      done.state === "pending_admin"
        ? `${done.display_name} is restored and waits for its owner again.`
        : `${done.display_name} is restored. Its members sign in again and its clients obtain new tokens.`,
    );
  };
  const pending = org?.state === "pending_admin";
  useShowOrganization(org); // the shell hides its organisation links while this one is deactivated

  if (!org) return error !== null ? <Alert tone="danger">{errorText(error)}</Alert> : null;
  return (
    <>
      <PageHeader
        title={
          <>
            {org.display_name} <span className="text-base text-muted">({stateLabel(org.state)})</span>
          </>
        }
        description={`${kindLabel(org.kind)} · ${org.id}`}
        actions={
          org.state === "inactive" ? undefined : (
            <>
              <LinkButton href={`/orgs/${org.id}/apps`}>Apps</LinkButton>
              <LinkButton href={`/orgs/${org.id}/agents`}>Agents</LinkButton>
              <LinkButton href={`/orgs/${org.id}/roles`}>Roles</LinkButton>
              <LinkButton href={`/orgs/${org.id}/brands`}>Brands</LinkButton>
            </>
          )
        }
      />
      <div className="flex flex-col gap-8">
        {org.state !== "inactive" && (
        <Section
          title={pending ? "Invite the owner" : "Invite an administrator"}
          description={pending ? `${org.display_name} becomes active when its owner accepts.` : undefined}
        >
          <div className="flex flex-col gap-3">
            <TextField label="Email" type="email" value={email} onChange={setEmail} />
            <div>
              <Button onPress={() => void invite()} isDisabled={!email}>
                Send invitation
              </Button>
            </div>
          </div>
        </Section>
        )}
        {org.state !== "inactive" && (
        <Section title="Webhook">
          {!hook ? (
            <div className="flex flex-col gap-3">
              <TextField label="Endpoint URL" value={hookUrl} onChange={setHookUrl} />
              <div>
                <Button onPress={() => void registerHook()}>Register endpoint</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {hook.secret && <p className="break-all">Secret (shown once): {hook.secret}</p>}
              <div>
                <Button onPress={() => void sendTest()}>Send test event</Button>
              </div>
            </div>
          )}
        </Section>
        )}
        {canDeactivate(org) && (
          <Section
            title="Deactivate this organisation"
            description="Its members and service clients lose access at once and its webhooks pause. Its records are kept. Only a platform admin can restore it."
          >
            <DeactivateOrganization org={org} run={run} onDone={deactivated} />
          </Section>
        )}
        {canRestore(org) && (
          <Section
            title="Restore this organisation"
            description="It returns to how it was: active if it has members (they sign in again), otherwise waiting for its owner. Only a platform admin can restore it."
          >
            <RestoreOrganization org={org} run={run} onDone={restored} />
          </Section>
        )}
        {message && <Alert tone="positive">{message}</Alert>}
        {error !== null && <Alert tone="danger">{errorText(error)}</Alert>}
      </div>
    </>
  );
}
